import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trophy } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';

/* Team-Challenges auf /coach (W3.4, 29.10.2026, docs/coach-cockpit/w3-challenge.md). Der Trainer legt für ein Team aus
   seinen Kunden eine Challenge an (Zeitraum, Wertung — Standard Schritte als Summe aus Apple Health/Tagebuch —, Ziel,
   Startnachricht). Mitglieder treten in der App bei (Opt-in); dort sehen sie nur Vorname + Initial. Hier sieht der
   Trainer die Rangliste mit vollem Namen. Pushes bei Start, Halbzeit und Ende schickt der Server. `client` = der
   axios-Client der Trainer-Seite (eigene Anmeldung). */

const fehlerText = (e, rest) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : rest);
const zahl = (v) => Number(v || 0).toLocaleString('de-DE');
const datum = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) : '');
/* Knöpfe und Chips wie im Mockup (Community.dc.html). */
const knopf = 'inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-[10px] border border-border bg-muted px-4 text-sm font-bold text-foreground transition-colors hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50';
const knopfHaupt = 'inline-flex min-h-[44px] items-center justify-center rounded-[10px] border border-primary bg-primary px-4 text-sm font-bold text-primary-foreground transition-colors hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50';
const PHASE = { kommt: 'startet bald', laeuft: 'läuft', beendet: 'beendet', abgesagt: 'abgesagt' };
const isoTag = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function leerFormular() {
  const morgen = new Date(); morgen.setDate(morgen.getDate() + 1);
  const ende = new Date(morgen); ende.setDate(ende.getDate() + 13);
  return { titel: '', art: 'schritte_summe', ziel: '100000', von: isoTag(morgen), bis: isoTag(ende), startnachricht: '', team: [] };
}

function Neu({ client, kunden, arten, onFertig, onAbbruch }) {
  const [f, setF] = useState(leerFormular);
  const [suche, setSuche] = useState('');
  const [busy, setBusy] = useState(false);
  const sichtbar = useMemo(() => kunden.filter((k) => k.name.toLowerCase().includes(suche.trim().toLowerCase())), [kunden, suche]);
  const umschalten = (id) => setF((x) => ({ ...x, team: x.team.includes(id) ? x.team.filter((t) => t !== id) : [...x.team, id] }));
  const speichern = async () => {
    setBusy(true);
    try {
      await client.post('/challenges', { ...f, ziel: Number(String(f.ziel).replace(/\D/g, '')), startnachricht: f.startnachricht || undefined });
      toast.success('Challenge angelegt – dein Team sieht sie in der App');
      onFertig();
    } catch (e) { toast.error(fehlerText(e, 'Nicht angelegt')); }
    setBusy(false);
  };
  return (
    <div className="space-y-3 rounded-[12px] border border-border bg-background p-3" data-testid="coach-challenge-neu">
      <Input aria-label="Titel" maxLength={80} placeholder="z. B. 300.000 Schritte im November" value={f.titel} onChange={(e) => setF({ ...f, titel: e.target.value })} className="h-11" />
      <div className="grid gap-2 sm:grid-cols-4">
        <label className="text-xs font-medium">Start<Input type="date" value={f.von} onChange={(e) => setF({ ...f, von: e.target.value })} className="mt-1 h-11" /></label>
        <label className="text-xs font-medium">Ende<Input type="date" value={f.bis} onChange={(e) => setF({ ...f, bis: e.target.value })} className="mt-1 h-11" /></label>
        <label className="text-xs font-medium">Wertung
          <select value={f.art} onChange={(e) => setF({ ...f, art: e.target.value })} className="mt-1 h-11 w-full rounded-md border border-input bg-background px-2 text-sm">
            {Object.entries(arten).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label className="text-xs font-medium">Ziel{f.art === 'schritte_summe' ? ' (Schritte)' : ' (Punkte)'}<Input inputMode="numeric" value={f.ziel} onChange={(e) => setF({ ...f, ziel: e.target.value })} className="mt-1 h-11" /></label>
      </div>
      <label className="block text-xs font-medium">Startnachricht (steht auf der Karte in der App)
        <textarea maxLength={500} rows={2} value={f.startnachricht} onChange={(e) => setF({ ...f, startnachricht: e.target.value })}
          className="mt-1 w-full rounded-md border border-input bg-background p-2 text-sm" placeholder="Kurz motivieren – ohne Zahlen über einzelne Personen" />
      </label>
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium">Team · {f.team.length} ausgewählt</span>
          <Input aria-label="Kunden suchen" placeholder="Name suchen" value={suche} onChange={(e) => setSuche(e.target.value)} className="ml-auto h-11 w-48" />
        </div>
        <div className="grid max-h-64 gap-1 overflow-y-auto sm:grid-cols-2" data-testid="coach-challenge-team">
          {sichtbar.map((k) => (
            <label key={k.id} className="flex min-h-11 cursor-pointer items-center gap-2 rounded-md border border-border/60 px-2 text-sm">
              <input type="checkbox" className="h-5 w-5 accent-marke" checked={f.team.includes(k.id)} onChange={() => umschalten(k.id)} />
              <span className="break-words">{k.name || 'ohne Namen'}</span>
            </label>
          ))}
          {!sichtbar.length && <p className="text-xs text-muted-foreground">Keine Kunden gefunden.</p>}
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground">Nur diese Personen sehen die Challenge und können in der App beitreten. In der Rangliste stehen nur Vorname und Initial; Pushes bei Start, Halbzeit und Ende nennen nur den Titel.</p>
      <div className="flex justify-end gap-2">
        <button type="button" className={knopf} onClick={onAbbruch}>Abbrechen</button>
        <button type="button" className={knopfHaupt} disabled={busy || f.titel.trim().length < 3 || !f.team.length} onClick={speichern}>Anlegen</button>
      </div>
    </div>
  );
}

function Rangliste({ client, cid }) {
  const [r, setR] = useState(null);
  useEffect(() => {
    client.get(`/challenges/${cid}/bestenliste`).then((x) => setR(x.data)).catch((e) => setR({ fehler: fehlerText(e, 'Nicht geladen') }));
  }, [client, cid]);
  if (!r) return <Skeleton className="h-16" />;
  if (r.fehler) return <p className="text-sm text-destructive">{r.fehler}</p>;
  if (!r.zeilen.length) return <p className="text-xs text-muted-foreground">Noch niemand beigetreten.</p>;
  return (
    <ol className="text-sm" data-testid="coach-challenge-rangliste">
      {r.zeilen.map((z) => (
        <li key={`${z.platz}-${z.name}`} className="grid grid-cols-[28px_minmax(0,1fr)_auto] items-center gap-2.5 border-t border-border/70 py-2 text-sm">
          <span className={`font-sans font-extrabold tabular-nums ${z.platz === 1 ? 'text-marke' : ''}`}>{z.platz}</span>
          <span className="min-w-0 break-words">{z.name}{z.geschafft && <span className="ml-2 rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-bold">Ziel</span>}</span>
          <strong className="tabular-nums">{zahl(z.stand)}</strong>
        </li>
      ))}
    </ol>
  );
}

export default function CoachTeamChallenges({ client }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [neu, setNeu] = useState(false);
  const [offen, setOffen] = useState(null);
  const lade = useCallback(() => {
    client.get('/challenges').then((r) => { setD(r.data); setErr(''); }).catch((e) => setErr(fehlerText(e, 'Challenges nicht geladen')));
  }, [client]);
  useEffect(() => { lade(); }, [lade]);
  const absagen = async (c) => {
    if (!window.confirm(`„${c.titel}“ absagen? Die App zeigt sie dann nicht mehr.`)) return;
    try { await client.patch(`/challenges/${c.id}`, { abgesagt: true }); toast.success('Abgesagt'); lade(); } catch (e) { toast.error(fehlerText(e, 'Nicht abgesagt')); }
  };
  return (
    <section className="rounded-[14px] border border-border bg-card px-5 py-[18px]" data-testid="coach-challenges" aria-label="Team-Challenges">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-sans text-base font-bold">Schritte-Challenge</h2>
          <p className="text-[13px] text-muted-foreground">Challenge für ein Team aus deinen Kunden – Teilnahme freiwillig in der App.</p>
        </div>
        {d && !neu && <button type="button" className={knopfHaupt} onClick={() => setNeu(true)} data-testid="coach-challenge-anlegen"><Plus className="mr-1 h-4 w-4" aria-hidden="true" /> Challenge</button>}
      </div>
      <div className="space-y-3">
        {err && <p className="text-sm text-destructive" role="alert">{err}</p>}
        {!d && !err && <Skeleton className="h-20" />}
        {d && neu && <Neu client={client} kunden={d.kunden} arten={d.arten} onAbbruch={() => setNeu(false)} onFertig={() => { setNeu(false); lade(); }} />}
        {d && !d.challenges.length && !neu && <p className="text-sm text-muted-foreground">Noch keine Team-Challenge.</p>}
        {d && d.challenges.map((c) => (
          <div key={c.id} className="space-y-2 border-t border-border/70 pt-3 first:border-t-0 first:pt-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="min-w-0 flex-1 break-words text-sm font-extrabold">{c.titel}</p>
              <span className={`inline-flex items-center rounded-full border px-[11px] py-[5px] text-xs font-bold ${c.phase === 'laeuft' ? 'border-marke bg-marke text-[#140c08]' : 'border-border bg-muted text-foreground/80'}`}>{PHASE[c.phase] || c.phase}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              {datum(c.von)}–{datum(c.bis)} · {c.phase === 'laeuft' ? `Tag ${c.tag} von ${c.tage}` : `${c.tage} Tage`} · {c.art_text} · Ziel {zahl(c.ziel)} · {c.teilnehmende} von {(c.team || []).length} dabei
            </p>
            {c.startnachricht && <p className="text-xs italic text-muted-foreground">„{c.startnachricht}“</p>}
            <div className="flex flex-wrap gap-2">
              <button type="button" className={knopf} aria-expanded={offen === c.id} onClick={() => setOffen(offen === c.id ? null : c.id)}><Trophy className="h-4 w-4" aria-hidden="true" /> Rangliste</button>
              {['kommt', 'laeuft'].includes(c.phase) && <button type="button" className={knopf} onClick={() => absagen(c)}>Absagen</button>}
            </div>
            {offen === c.id && <Rangliste client={client} cid={c.id} />}
          </div>
        ))}
      </div>
    </section>
  );
}
